import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { BeforeAfterSlider } from "@/components/site/BeforeAfter";
import { PageHeader, Empty } from "@/components/portal/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FILE_CLS = "text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#0E1F3F] file:px-4 file:py-2 file:text-white";

export default function TransformationsAdmin() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  useEffect(() => {
    api("get", "/transformations").then(setItems);
  }, [api]);

  const upload = async (e) => {
    e.preventDefault();
    const b = beforeRef.current?.files?.[0];
    const a = afterRef.current?.files?.[0];
    if (!b || !a) return toast.error("Choose both a before and an after photo");
    const fd = new FormData();
    fd.append("before", b);
    fd.append("after", a);
    fd.append("title", title);
    fd.append("caption", caption);
    setBusy(true);
    try {
      const t = await api("post", "/transformations", fd);
      setItems([t, ...(items || [])]);
      setTitle("");
      setCaption("");
      beforeRef.current.value = "";
      afterRef.current.value = "";
      toast.success("Before & after is live on the website");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.title || "this pair"}" from the website?`)) return;
    try {
      await api("delete", `/transformations/${t.id}`);
      setItems(items.filter((x) => x.id !== t.id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="ba-admin-page">
      <PageHeader eyebrow="Website" title="Before & after" />
      <form onSubmit={upload} className="mb-8 grid gap-4 rounded-2xl border border-[#E6E2DA] bg-white p-5 sm:grid-cols-2" data-testid="ba-upload-form">
        <div className="space-y-1.5"><Label className="text-xs">Before photo</Label><input ref={beforeRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className={FILE_CLS} data-testid="ba-before-input" /></div>
        <div className="space-y-1.5"><Label className="text-xs">After photo</Label><input ref={afterRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className={FILE_CLS} data-testid="ba-after-input" /></div>
        <Input placeholder="Title (e.g. Stamped concrete driveway)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ba-title-input" />
        <Input placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="ba-caption-input" />
        <button disabled={busy} data-testid="ba-upload-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F2B91C] px-4 py-2.5 text-sm font-bold text-[#0E1F3F] hover:bg-[#D9A30F] disabled:opacity-60 sm:col-span-2"><Upload size={15} />{busy ? "Uploading…" : "Add before & after"}</button>
      </form>
      {items && !items.length && <Empty text="No before & after pairs yet. Tip: take both photos from the same spot and angle." />}
      <div className="grid gap-6 md:grid-cols-2">
        {items?.map((t) => (
          <div key={t.id} className="rounded-2xl border border-[#E6E2DA] bg-white p-4">
            <BeforeAfterSlider t={t} />
            <button onClick={() => remove(t)} data-testid={`ba-delete-${t.id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#8C877D] hover:text-red-600"><Trash2 size={14} />Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}
